import { KeyboardEvent, ReactNode, memo, useMemo } from 'react'

import { Search } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  DialogContent,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { debounce } from 'lodash-es'

import { fetchNextPageOnBottomReached } from '@shared/components/MaterialUIEnhancements/AutoComplete'
import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'

/**
 * The subset of an infinite query the list needs. Kept structural rather than derived from
 * `UseInfiniteQueryResult` so a picker can merge several queries into one logical list.
 */
export type PickerDialogListQuery = {
  isLoading: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => Promise<unknown>
}

export type PickerDialogContentProps<T> = {
  items: T[]
  /** Drives the loading, empty and load-more states of the list. */
  listQuery: PickerDialogListQuery
  /** Debounced — feed it into the list query to search server-side. */
  onQueryChange: (query: string) => void
  searchPlaceholder: string
  emptyText: string
  getKey: (item: T) => string
  getPrimary: (item: T) => ReactNode
  getSecondary?: (item: T) => ReactNode
  isSelected?: (item: T) => boolean
  isDisabled?: (item: T) => boolean
  /** The dialog closes itself right after. */
  onSelect: (item: T) => void
}

const PickerDialogContentRaw = <T,>({
  items,
  listQuery,
  onQueryChange,
  searchPlaceholder,
  emptyText,
  getKey,
  getPrimary,
  getSecondary,
  isSelected,
  isDisabled,
  onSelect,
}: PickerDialogContentProps<T>) => {
  const handleClose = useCloseHandler()

  const debouncedQueryChange = useMemo(
    () => debounce((value: string) => onQueryChange(value), 400, { leading: true }),
    [onQueryChange],
  )

  /**
   * A picker is often opened from within a form. The dialog is portaled out of the DOM but stays
   * inside that form in the React tree, so an Enter press here would bubble synthetically into it
   * and submit it.
   */
  const handleSearchKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  return (
    <DialogContent>
      <TextField
        size="small"
        fullWidth
        autoFocus
        placeholder={searchPlaceholder}
        onChange={(event) => debouncedQueryChange(event.target.value)}
        onKeyDown={handleSearchKeyDown}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      {listQuery.isLoading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : !items.length ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <Typography variant="text-sm" sx={{ color: 'text.secondary' }}>
            {emptyText}
          </Typography>
        </Stack>
      ) : (
        <Box
          sx={{ mt: 1, maxHeight: '50vh', overflowY: 'auto' }}
          onScroll={fetchNextPageOnBottomReached({
            hasNextPage: listQuery.hasNextPage,
            isFetchingNextPage: listQuery.isFetchingNextPage,
            fetchNextPage: listQuery.fetchNextPage,
          })}
        >
          <List disablePadding>
            {items.map((item) => (
              <ListItemButton
                key={getKey(item)}
                selected={isSelected?.(item)}
                disabled={isDisabled?.(item)}
                onClick={() => {
                  onSelect(item)
                  handleClose()
                }}
              >
                <ListItemText primary={getPrimary(item)} secondary={getSecondary?.(item)} />
              </ListItemButton>
            ))}
          </List>
          {listQuery.isFetchingNextPage && (
            <Stack alignItems="center" sx={{ py: 1 }}>
              <CircularProgress size={20} />
            </Stack>
          )}
        </Box>
      )}
    </DialogContent>
  )
}

export const PickerDialogContent = memo(PickerDialogContentRaw) as typeof PickerDialogContentRaw
